import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { useSession } from "../lib/auth.ts";
import { fmtDurationMs, api, testResultLabel, testResultPillClass, testTypeLabel } from "../lib/api.ts";
import { TestChart } from "../components/TestChart.tsx";
import { TestParameters } from "../components/TestParameters.tsx";
import { UptickPushCard } from "../components/UptickPushCard.tsx";

const TEST_TYPE_ECAP = 6;

export function TestDetailPage() {
  const { testId } = useParams<{ testId: string }>();
  const { data: session } = useSession();
  const orgId = session?.session.activeOrganizationId;
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: ["test", orgId, testId],
    queryFn: () => api.testDetail(orgId!, testId!),
    enabled: !!orgId && !!testId,
  });

  const samples = useQuery({
    queryKey: ["test-data", orgId, testId],
    queryFn: () => api.testData(orgId!, testId!),
    enabled: !!orgId && !!testId,
    staleTime: 5 * 60 * 1000, // blobs are immutable post-sync
    retry: (count, err) => {
      // 404 = "no chart blob and no samples to project from" — don't retry.
      const msg = (err as Error).message ?? "";
      if (msg.startsWith("not-found") || msg.includes("404")) return false;
      return count < 3;
    },
  });

  if (detail.isLoading) return <p className="muted">Loading…</p>;
  if (detail.error) return <p className="err">Couldn't load test — {(detail.error as Error).message}</p>;
  if (!detail.data) return null;

  const t = detail.data.test;
  let params: Record<string, unknown> = {};
  try { params = JSON.parse(t.paramsJson); } catch { /* leave empty */ }

  // ECAP is a capacity CALCULATOR — only a MEASURED run has a clamp-current
  // trace worth charting; a manual entry has no series, so hide the chart
  // section entirely. Its curve is current (mA), not a discharge voltage curve.
  const isEcap       = t.testType === TEST_TYPE_ECAP;
  const ecapMeasured = isEcap && Number(params.measured) === 1;
  const showChart    = !isEcap || ecapMeasured;
  const chartHeading = isEcap ? "Standby / Alarm current" : "Discharge curve";

  const autoLabel = `${testTypeLabel(t.testType)} — ${new Date(t.startedAt).toLocaleString()}`;
  const displayName = t.name && t.name.trim() !== "" ? t.name : autoLabel;

  return (
    <>
      <Link to="/tests">← Back to tests</Link>
      <TestNameHeader
        orgId={orgId!}
        testId={t.id}
        currentName={t.name}
        autoLabel={autoLabel}
        displayName={displayName}
        onSaved={() => qc.invalidateQueries({ queryKey: ["test", orgId, testId] })}
      />

      <div className="card">
        <div className="kv-grid">
          <span className="muted">Result</span>
          <span><span className={testResultPillClass(t.testResult)}>{testResultLabel(t.testResult)}</span></span>

          <span className="muted">Duration</span>
          <span>{fmtDurationMs(t.durationMs)}</span>

          <span className="muted">Cloud ID</span>
          <code>{t.id}</code>

          <span className="muted">Device</span>
          <span>
            {t.deviceName
              ? <>{t.deviceName} <code className="muted" style={{ marginLeft: "0.5rem" }}>{t.deviceId}</code></>
              : <code>{t.deviceId}</code>}
          </span>

          <span className="muted">Device test #</span>
          <span>{t.deviceTestId}</span>

          {typeof params.battery_id === "string" && params.battery_id && (
            <>
              <span className="muted">Battery ID</span>
              <span><Link to={`/batteries/${params.battery_id}`}>{params.battery_id}</Link></span>
            </>
          )}

          <span className="muted">Attestation</span>
          <span>{t.attestationStatus}</span>
        </div>
      </div>

      {showChart && (
        <>
          <h3>{chartHeading}</h3>
          <div className="card">
            {samples.isLoading
              ? <p className="muted">Loading samples…</p>
              : samples.error
                ? (((samples.error as Error).message ?? "").includes("not-found")
                    ? <p className="muted">No samples on file.</p>
                    : <p className="err">Couldn't load samples — {(samples.error as Error).message}</p>)
                : <TestChart points={samples.data ?? []} testType={t.testType} params={params} />}
          </div>
        </>
      )}

      <h3>Test Parameters</h3>
      <div className="card">
        <TestParameters test={t} params={params} />
      </div>

      <h3>Files</h3>
      <div className="card">
        {t.hasPdf
          ? <p><a href={api.testPdfUrl(orgId!, t.id)} target="_blank" rel="noreferrer">PDF report</a></p>
          : <p className="muted">No PDF report on file.</p>}
      </div>

      <UptickPushCard orgId={orgId!} testId={t.id} />
    </>
  );
}

// Inline-rename header. Click the pencil to edit; Enter saves, Escape
// cancels. The displayed value falls back to an auto-label so the row is
// never blank, but the underlying `name` stays null until the user types
// one — auto-label drift is intentional, the user can always override.
function TestNameHeader({
  orgId, testId, currentName, autoLabel, displayName, onSaved,
}: {
  orgId:       string;
  testId:      string;
  currentName: string | null;
  autoLabel:   string;
  displayName: string;
  onSaved:     () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(currentName ?? "");
  const [error,   setError]   = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (name: string) => api.renameTest(orgId, testId, name),
    onSuccess:  () => { setEditing(false); setError(null); onSaved(); },
    onError:    (e: Error) => setError(e.message),
  });

  if (!editing) {
    return (
      <h1 style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <span>{displayName}</span>
        <button
          type="button"
          className="btn-link"
          aria-label="Rename test"
          onClick={() => { setDraft(currentName ?? ""); setEditing(true); setError(null); }}
        >
          ✎
        </button>
      </h1>
    );
  }

  const onSave = () => {
    const trimmed = draft.trim();
    if (trimmed.length === 0) { setError("Name can't be empty"); return; }
    if (trimmed.length > 200) { setError("Name too long (max 200)"); return; }
    if (trimmed === (currentName ?? "")) { setEditing(false); return; }
    mutation.mutate(trimmed);
  };

  return (
    <div style={{ margin: "1rem 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <input
          autoFocus
          type="text"
          value={draft}
          placeholder={autoLabel}
          maxLength={200}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter")  onSave();
            if (e.key === "Escape") setEditing(false);
          }}
          style={{ flex: 1, fontSize: "1.4rem", padding: "0.3rem 0.5rem" }}
        />
        <button type="button" onClick={onSave} disabled={mutation.isPending}>
          {mutation.isPending ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={() => setEditing(false)}>Cancel</button>
      </div>
      {error && <p className="err" style={{ marginTop: "0.4rem" }}>{error}</p>}
    </div>
  );
}
